import { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Alert, Stack, IconButton } from '@mui/material';
import { Save, Delete } from '@mui/icons-material';
import cmsService, { type SettingsApiData } from '../../services/cms.service';

interface AboutUsEditorProps {
  /** `about` field from settings API (HTML). */
  initialHtml: string;
  /** Called with the updated document after a successful save so the parent cache stays in sync. */
  onSettingsUpdated?: (doc: SettingsApiData) => void;
  /** When false, editor is read-only and save / clear controls are hidden. Defaults to true. */
  canEdit?: boolean;
}

export default function AboutUsEditor({ initialHtml, onSettingsUpdated, canEdit = true }: Readonly<AboutUsEditorProps>) {
  const [html, setHtml] = useState(initialHtml);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setHtml(initialHtml);
  }, [initialHtml]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const doc = await cmsService.updatePage('about_us', { content: html });
      onSettingsUpdated?.(doc);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const dirty = html !== initialHtml;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          About Us saved. The app will show this HTML on the public About page.
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Body (HTML)
        </Typography>
        {canEdit ? (
          <IconButton
            size="small"
            color="error"
            title="Clear body"
            aria-label="Clear body"
            disabled={!html.trim() || saving}
            onClick={() => setHtml('')}
          >
            <Delete fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
      <TextField
        fullWidth
        multiline
        rows={14}
        value={html}
        onChange={(e) => setHtml(e.target.value)}
        placeholder="<p>About our app.</p>"
        slotProps={{
          input: { readOnly: !canEdit },
        }}
        sx={{
          mb: 2,
          '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 },
        }}
      />

      {canEdit ? (
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || !dirty}>
          {saving ? 'Saving...' : 'Save About Us'}
        </Button>
      ) : null}
    </Box>
  );
}
